import { supabase } from './supabase'
import type {
  User,
  Project,
  ProjectDetail,
  StyleReference,
  Generation,
  GenerationDetail,
  PromptInputs,
  SubscriptionInfo,
  SubscriptionTier
} from '../types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// ============================================
// Simple in-memory cache with TTL
// ============================================
const cache = new Map<string, { data: any; timestamp: number }>()

// Cache TTL constants (in milliseconds)
const CACHE_TTL = {
  USER: 30000,        // 30 seconds - credits change on generation
  SUBSCRIPTION: 60000, // 60 seconds - rarely changes
  PROJECTS: 30000,    // 30 seconds - user may create/delete
  STYLE_REFS: 60000,  // 60 seconds - rarely changes
} as const

function getCached<T>(key: string, ttl: number): T | null {
  const entry = cache.get(key)
  if (entry && Date.now() - entry.timestamp < ttl) {
    return entry.data as T
  }
  return null
}

function setCache(key: string, data: any): void {
  cache.set(key, { data, timestamp: Date.now() })
}

export function clearCache(key?: string): void {
  if (key) {
    cache.delete(key)
  } else {
    cache.clear()
  }
}

// Cache keys
export const CACHE_KEYS = {
  USER: 'user:me',
  SUBSCRIPTION: 'stripe:subscription',
  PROJECTS: 'projects:all',
  STYLE_REFS: 'styleRefs:all',
} as const

// Helper to get auth headers
async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  }
}

// Helper for API requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// User API
export const userApi = {
  getMe: async (forceRefresh = false): Promise<User> => {
    if (!forceRefresh) {
      const cached = getCached<User>(CACHE_KEYS.USER, CACHE_TTL.USER)
      if (cached) return cached
    }
    const user = await apiRequest<User>('/api/users/me')
    setCache(CACHE_KEYS.USER, user)
    return user
  },

  getCachedUser: (): User | null => {
    return getCached<User>(CACHE_KEYS.USER, CACHE_TTL.USER)
  },

  updateProfile: async (data: { name?: string; avatarUrl?: string }) => {
    const user = await apiRequest<User>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    setCache(CACHE_KEYS.USER, user)
    return user
  },

  getCredits: () => apiRequest<{
    credits: number
    monthlyCredits: number
    plan: string
    usagePercentage: number
  }>('/api/users/me/credits'),
}

// Projects API
export const projectsApi = {
  getAll: async (forceRefresh = false): Promise<Project[]> => {
    if (!forceRefresh) {
      const cached = getCached<Project[]>(CACHE_KEYS.PROJECTS, CACHE_TTL.PROJECTS)
      if (cached) return cached
    }
    const projects = await apiRequest<Project[]>('/api/projects')
    setCache(CACHE_KEYS.PROJECTS, projects)
    return projects
  },

  getCachedProjects: (): Project[] | null => {
    return getCached<Project[]>(CACHE_KEYS.PROJECTS, CACHE_TTL.PROJECTS)
  },

  getOne: (id: string) => apiRequest<ProjectDetail>(`/api/projects/${id}`),

  create: async (data: { name: string; description?: string }) => {
    const project = await apiRequest<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    clearCache(CACHE_KEYS.PROJECTS) // Invalidate projects cache
    return project
  },

  update: (id: string, data: { name?: string; description?: string }) =>
    apiRequest<Project>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: async (id: string) => {
    await apiRequest<void>(`/api/projects/${id}`, { method: 'DELETE' })
    clearCache(CACHE_KEYS.PROJECTS) // Invalidate projects cache
  },
}

// Style References API
export const styleRefsApi = {
  getAll: async (forceRefresh = false): Promise<StyleReference[]> => {
    if (!forceRefresh) {
      const cached = getCached<StyleReference[]>(CACHE_KEYS.STYLE_REFS, CACHE_TTL.STYLE_REFS)
      if (cached) return cached
    }
    const refs = await apiRequest<StyleReference[]>('/api/style-references')
    setCache(CACHE_KEYS.STYLE_REFS, refs)
    return refs
  },

  getCachedStyleRefs: (): StyleReference[] | null => {
    return getCached<StyleReference[]>(CACHE_KEYS.STYLE_REFS, CACHE_TTL.STYLE_REFS)
  },

  getOne: (id: string) => apiRequest<StyleReference>(`/api/style-references/${id}`),

  // Get image as base64 via backend proxy (avoids CORS issues with R2)
  getImageData: (id: string) =>
    apiRequest<{ base64: string }>(`/api/style-references/${id}/image-data`),

  create: async (data: { name: string; description?: string; imageBase64: string }) => {
    const ref = await apiRequest<StyleReference>('/api/style-references', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    clearCache(CACHE_KEYS.STYLE_REFS) // Invalidate cache
    return ref
  },

  update: (id: string, data: { name?: string; description?: string }) =>
    apiRequest<StyleReference>(`/api/style-references/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: async (id: string) => {
    await apiRequest<void>(`/api/style-references/${id}`, { method: 'DELETE' })
    clearCache(CACHE_KEYS.STYLE_REFS) // Invalidate cache
  },
}

// Unseen generation for notifications
export interface UnseenGeneration {
  id: string
  productDescription: string | null
  thumbnail: string | null
  completedAt: string | null
  createdAt: string
}

// Generations API
export const generationsApi = {
  getAll: (projectId?: string, limit?: number) => {
    const params = new URLSearchParams()
    if (projectId) params.set('projectId', projectId)
    if (limit) params.set('limit', limit.toString())
    const queryString = params.toString()
    return apiRequest<Generation[]>(`/api/generations${queryString ? `?${queryString}` : ''}`)
  },

  getOne: (id: string) => apiRequest<GenerationDetail>(`/api/generations/${id}`),

  create: (data: {
    projectId?: string
    styleReferenceId?: string
    inputs: PromptInputs
  }) =>
    apiRequest<{ id: string; status: string; creditsUsed: number }>(
      '/api/generations/generate',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  edit: (data: { imageBase64: string; instruction: string }) =>
    apiRequest<{ imageUrl: string; imageBase64: string }>(
      '/api/generations/edit',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  getUnseen: () =>
    apiRequest<{ count: number; generations: UnseenGeneration[] }>(
      '/api/generations/unseen'
    ),

  markViewed: (generationIds: string[] | 'all') =>
    apiRequest<{ success: boolean }>('/api/generations/mark-viewed', {
      method: 'POST',
      body: JSON.stringify({ generationIds }),
    }),

  update: (id: string, data: { projectId?: string | null }) =>
    apiRequest<{ id: string; projectId: string | null; projectName: string | null }>(
      `/api/generations/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/api/generations/${id}`, {
      method: 'DELETE',
    }),
}

// Polling helper for generation status with tab visibility handling and retry logic
export async function pollGeneration(
  id: string,
  onUpdate: (detail: GenerationDetail) => void,
  interval: number = 1000
): Promise<GenerationDetail> {
  const MAX_CONSECUTIVE_ERRORS = 3

  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let isPolling = true
    let consecutiveErrors = 0

    const poll = async () => {
      if (!isPolling) return

      try {
        const detail = await generationsApi.getOne(id)
        consecutiveErrors = 0 // Reset on success
        onUpdate(detail)

        if (detail.status === 'COMPLETED') {
          cleanup()
          resolve(detail)
        } else if (detail.status === 'FAILED') {
          cleanup()
          reject(new Error(detail.errorMessage || 'Generation failed'))
        } else if (isPolling) {
          timeoutId = setTimeout(poll, interval)
        }
      } catch (error) {
        consecutiveErrors++
        console.warn(`Poll error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error)

        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          cleanup()
          reject(error)
        } else if (isPolling) {
          // Retry with increasing backoff: 2s, 4s, 6s
          const backoff = consecutiveErrors * 2000
          timeoutId = setTimeout(poll, backoff)
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - clear pending timeout (will resume on focus)
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      } else {
        // Tab visible again - immediately poll to catch up
        if (isPolling && !timeoutId) {
          poll()
        }
      }
    }

    const cleanup = () => {
      isPolling = false
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }

    // Listen for tab visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Start polling
    poll()
  })
}

// Stripe API
export const stripeApi = {
  // Get current subscription status
  getSubscription: async (forceRefresh = false): Promise<SubscriptionInfo> => {
    if (!forceRefresh) {
      const cached = getCached<SubscriptionInfo>(CACHE_KEYS.SUBSCRIPTION, CACHE_TTL.SUBSCRIPTION)
      if (cached) return cached
    }
    const sub = await apiRequest<SubscriptionInfo>('/api/stripe/subscription')
    setCache(CACHE_KEYS.SUBSCRIPTION, sub)
    return sub
  },

  getCachedSubscription: (): SubscriptionInfo | null => {
    return getCached<SubscriptionInfo>(CACHE_KEYS.SUBSCRIPTION, CACHE_TTL.SUBSCRIPTION)
  },

  // Create checkout session for subscription
  checkoutSubscription: async (tier: Exclude<SubscriptionTier, 'FREE'>) => {
    const result = await apiRequest<{ url: string }>('/api/stripe/checkout/subscription', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    })
    // Clear cache - subscription might change after checkout
    clearCache(CACHE_KEYS.SUBSCRIPTION)
    clearCache(CACHE_KEYS.USER)
    return result
  },

  // Create checkout session for credit pack
  checkoutCredits: async (packType: 'small' | 'medium' | 'large') => {
    const result = await apiRequest<{ url: string }>('/api/stripe/checkout/credits', {
      method: 'POST',
      body: JSON.stringify({ packType }),
    })
    // Clear cache - credits might change after purchase
    clearCache(CACHE_KEYS.SUBSCRIPTION)
    clearCache(CACHE_KEYS.USER)
    return result
  },

  // Create customer portal session
  openPortal: () =>
    apiRequest<{ url: string }>('/api/stripe/portal', {
      method: 'POST',
    }),
}
